import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmarkCircle } from '@fortawesome/free-regular-svg-icons';
import { themes as commonThemes, utils as commonUtils } from '@sitebuilder/common';
import { StateShape as ReduxStateShape } from '../../../reducers';
import { resizeSidebar } from '../../CustomSchemaWidgets/utils';
import ColorVarSelect from './ColorVarSelect';
import ColorSelect from './ColorSelect';

export interface ThemeColorPickerProps {
    label: string;
    value: string;
    colorKey: string;
    uiSchemaOptions: any;
    onChange: (value: any) => void;
}

export const onClickClose = () => {
    const one = document.querySelector<HTMLElement>('.color-picker-sidebar');
    let moving = document.querySelector<HTMLElement>('.moving')?.nextElementSibling || null;
    if (moving === null) {
        moving = document.querySelector<HTMLElement>('.color-picker-input-selection');
    }
    const newChildren = one?.children;
    const selectors = document.querySelectorAll<HTMLElement>('.color-picker-selector');
    selectors.forEach((selector) => {
        selector.classList.remove('moving');
    });
    if (newChildren?.length) {
        Array.prototype.forEach.call(newChildren, function (child) {
            moving?.append(child);
        });
    }
};

const ThemeColorPicker = (props: ThemeColorPickerProps) => {
    const { uiSchemaOptions, onChange, label, value, colorKey } = props;
    const [showVarDropDown, setShowVarDropDown] = useState(false);
    const [hexColor, setHexColor] = useState('');
    const [rgbColor, setRgbColor] = useState('');
    const [selectedColorVar, setSelectedColorVar] = useState('');

    const { theme } = useSelector((state: ReduxStateShape) => ({
        ...state?.themes,
    }));

    const setHexForSelectedVar = (colorVariable: string) => {
        const colors: any = {
            ...theme?.colors,
            ...commonThemes.defaultColors,
            ...theme?.defaultColors,
        };
        if (colorVariable && colors?.[colorVariable]) {
            let hexValue = '';
            if (colors?.[colorVariable]?.hex) {
                hexValue = colors?.[colorVariable]?.hex;
            } else if (colors?.[colorVariable]?.rgb) {
                hexValue = commonUtils.RGBToHex(colors?.[colorVariable]?.rgb);
            } else if (colors?.[colorVariable].includes('var')) {
                const colorVariableNextLevel = colors?.[colorVariable]
                    ?.replace('var(', '')
                    ?.replace(')', '')
                    ?.replace(/['"]+/g, '');
                setHexForSelectedVar(colorVariableNextLevel);
                return null;
            }
            setHexColor(hexValue === 'null' ? '' : hexValue);
        }
    };

    const setDefaultVariableValue = () => {
        setShowVarDropDown(true);
        const colorVariable = value?.replace('var(', '')?.replace(')', '')?.replace(/['"]+/g, '');
        setHexForSelectedVar(colorVariable);
        setSelectedColorVar(colorVariable);
    };

    useEffect(() => {
        if (!showVarDropDown) {
            setSelectedColorVar('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showVarDropDown]);

    useEffect(() => {
        if (value) {
            if (commonUtils.isJSON(value)) {
                const jsonValue = (value && JSON.parse(value)) || '';
                if (jsonValue && typeof jsonValue === 'string') {
                    setDefaultVariableValue();
                }
            } else {
                setDefaultVariableValue();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        setHexForSelectedVar(selectedColorVar);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedColorVar]);

    const showColorSelection = (e: any) => {
        const one = document.querySelector<HTMLElement>('.color-picker-sidebar');
        const moving = document.querySelector<HTMLElement>('.moving')?.nextElementSibling || null;
        const newChildren = one?.children;
        const selectors = document.querySelectorAll<HTMLElement>('.color-picker-selector');
        selectors.forEach((selector) => {
            selector.classList.remove('moving');
        });

        if (moving !== null) {
            Array.prototype.forEach.call(newChildren, function (child) {
                moving?.append(child);
            });
        }
        e.target.classList.add('moving');
        const children = e.target.nextElementSibling.children;
        Array.prototype.forEach.call(children, function (child) {
            one?.append(child);
        });
        resizeSidebar();
    };

    return (
        <div className="theme-color-picker">
            <label id="color-picker-label">{label}</label>
            <div className="color-picker-selector" onClick={(event) => showColorSelection(event)}>
                <input type="color" className="color-picker-show" value={hexColor} />
                {selectedColorVar || rgbColor ? (
                    <span>{selectedColorVar || rgbColor}</span>
                ) : (
                    <span className="color-placeholder">Select Color</span>
                )}
            </div>
            <div className="color-picker-input-selection">
                <div className="color-picker-input">
                    <button className="close-button" onClick={onClickClose}>
                        <FontAwesomeIcon icon={faXmarkCircle} size={'lg'} />
                    </button>
                    <ColorVarSelect
                        options={uiSchemaOptions}
                        onChangeShowVarOption={(e: any) => setShowVarDropDown(e.target.checked)}
                        showVarDropDown={showVarDropDown}
                        selectedColorVar={selectedColorVar}
                        setSelectedColorVar={setSelectedColorVar}
                        theme={theme}
                        value={value}
                        colorKey={colorKey}
                        onChange={onChange}
                    />
                    {!showVarDropDown && (
                        <ColorSelect
                            value={value}
                            hexColor={hexColor}
                            rgbColor={rgbColor}
                            setHexColor={setHexColor}
                            setRgbColor={setRgbColor}
                            onChange={onChange}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ThemeColorPicker;
